import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, ListOrdered, Calendar, GitBranch, MapPin, Megaphone, UserPlus, X, Check, Loader2, Clock, UserCog, ShieldAlert, HeartPulse, Share2 } from 'lucide-react';
import { api, apiError } from './api';
import { coverSrc, Crest } from './Tournaments';
import { Celebration, computeChampion } from './Celebration';

const TABS = [
  { id: 'standings', label: 'Standings', icon: <ListOrdered size={15} /> },
  { id: 'overview', label: 'Overview', icon: <GitBranch size={15} /> },
  { id: 'fixtures', label: 'Fixtures', icon: <Calendar size={15} /> },
  { id: 'safety', label: 'Safety', icon: <ShieldAlert size={15} /> },
  { id: 'news', label: 'News', icon: <Megaphone size={15} /> },
];

const roundLabel = (n) => ({ 1: 'Final', 2: 'Semi-finals', 4: 'Quarter-finals', 8: 'Round of 16' }[n] || 'Knockout round');
const CUR = { gbp: '£', eur: '€', usd: '$' };
const feeLabel = (t) => Number(t?.entry_fee_amount) > 0 ? `${CUR[t.entry_currency] || ''}${t.entry_fee_amount}` : (t?.entry_fee || '');

export default function PublicPortal() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [tab, setTab] = useState('standings');
  const [showEnter, setShowEnter] = useState(false);
  const [entryForm, setEntryForm] = useState({ team_name: '', manager: '', contact: '', notes: '' });
  const [entryBusy, setEntryBusy] = useState(false);
  const [entryDone, setEntryDone] = useState(false);
  const [entryErr, setEntryErr] = useState('');
  const [payStatus, setPayStatus] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareTeam, setShareTeam] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (!sid) return;
    setPayStatus('checking');
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/checkout/status/${sid}`);
        if (data.payment_status === 'paid') { setPayStatus('paid'); return; }
        if (data.status === 'expired') { setPayStatus('failed'); return; }
      } catch (err) { /* retry */ }
      if (attempts++ < 8) setTimeout(poll, 2000); else setPayStatus('timeout');
    };
    poll();
  }, []);

  const submitEntry = async (e) => {
    e.preventDefault();
    setEntryErr(''); setEntryBusy(true);
    try {
      const { data } = await api.post(`/public/tournaments/${id}/enter`, entryForm);
      if (Number(t?.entry_fee_amount) > 0) {
        const co = await api.post(`/public/entries/${data.id}/checkout`, { origin_url: window.location.origin });
        window.location.href = co.data.url;
        return;
      }
      setEntryDone(true);
    } catch (e2) { setEntryErr(apiError(e2)); }
    setEntryBusy(false);
  };

  useEffect(() => {
    const fetchData = () => api.get(`/public/tournaments/${id}`).then((r) => { setData(r.data); setStatus('ok'); }).catch(() => setStatus((s) => (s === 'loading' ? 'error' : s)));
    fetchData();
    const iv = setInterval(fetchData, 20000); // real-time refresh (in sync with RefControl)
    return () => clearInterval(iv);
  }, [id]);

  const teamMap = useMemo(() => Object.fromEntries((data?.teams || []).map((x) => [x.id, x])), [data]);
  const teamName = (tid) => (tid ? (teamMap[tid]?.name || '—') : 'TBD');
  const offName = (oid) => (data?.officials || []).find((o) => o.id === oid)?.name;
  const [champDismissed, setChampDismissed] = useState(false);
  const champion = useMemo(() => (data ? computeChampion(data.fixtures, data.tournament, teamMap) : null), [data, teamMap]);
  const champKey = champion ? `rc-champ-${id}-${champion.id}` : null;
  const showChamp = !!champion && !champDismissed && !(champKey && typeof localStorage !== 'undefined' && localStorage.getItem(champKey));

  if (status === 'loading') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Loading…</div>;
  if (status === 'error') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Tournament not found.</div>;

  const t = data.tournament;
  const completed = data.fixtures.filter((f) => f.status === 'completed');
  const scheduled = data.fixtures.filter((f) => f.status !== 'completed');
  const ko = data.fixtures.filter((f) => f.stage === 'knockout');
  const koSections = {};
  ko.forEach((f) => {
    const key = f.round_name || `Round ${f.round}`;
    if (!koSections[key]) koSections[key] = { order: f.round_order || f.round, items: [] };
    koSections[key].items.push(f);
  });
  const koSorted = Object.entries(koSections).sort((a, b) => a[1].order - b[1].order);
  const groupKeys = [...new Set(data.standings.map((r) => r.group).filter(Boolean))].sort();

  const scheduleText = (() => {
    const header = shareTeam ? `⚽ ${teamName(shareTeam)} — ${t.name}` : `⚽ ${t.name} — Schedule`;
    const lines = [header, ''];
    let fxs = [...data.fixtures].sort((a, b) => (a.kickoff || 'zz').localeCompare(b.kickoff || 'zz'));
    if (shareTeam) fxs = fxs.filter((f) => f.home_team_id === shareTeam || f.away_team_id === shareTeam);
    fxs.forEach((f) => {
      const time = f.kickoff || 'TBC';
      const pitch = f.pitch ? ` · ${f.pitch}` : '';
      const home = f.home_team_id ? teamName(f.home_team_id) : (f.home_placeholder || 'TBD');
      const away = f.away_team_id ? teamName(f.away_team_id) : (f.away_placeholder || 'TBD');
      const score = f.status === 'completed' ? `  (${f.home_score}-${f.away_score})` : '';
      lines.push(`${time}${pitch} — ${home} v ${away}${score}`);
    });
    if (fxs.length === 0) lines.push('No fixtures scheduled yet.');
    lines.push('', `Live scores & standings: ${window.location.origin}/live/${id}`);
    return lines.join('\n');
  })();

  const nativeShare = async () => {
    try { if (navigator.share) await navigator.share({ title: `${t.name} schedule`, text: scheduleText }); } catch {}
  };
  const copyText = () => { navigator.clipboard.writeText(scheduleText); setShareCopied(true); setTimeout(() => setShareCopied(false), 1800); };

  return (
    <div className="ttheme min-h-screen bg-[#0A0A0A] text-white font-sans pb-12">
      <div className="max-w-3xl mx-auto px-5">
        {t.cover_photo && (
          <div className="mt-5 rounded-2xl overflow-hidden h-44 sm:h-56 relative" data-testid="public-cover">
            <img src={coverSrc(t.cover_photo)} alt={t.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F4F6F9] via-[#F4F6F9]/30 to-transparent" />
          </div>
        )}
        <div className="py-8 text-center border-b border-gray-800">
          {!t.cover_photo && <Trophy className="w-10 h-10 text-[#00BFFF] mx-auto mb-3" />}
          <h1 className="text-3xl font-black tracking-wide">{t.name}</h1>
          <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
            {t.location && <span className="flex items-center gap-1"><MapPin size={12}/>{t.location}</span>}
            <span className="uppercase tracking-widest">{t.format.replace('_', ' + ')}</span>
          </div>
          {(t.age_group || t.gender || t.standard) && (
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {[t.age_group, t.gender === 'girls' ? 'Girls' : (t.gender === 'mixed' ? 'Mixed' : ''), t.standard && `${t.standard} standard`].filter(Boolean).map((c) => (
                <span key={c} className="text-[10px] bg-[#00BFFF]/15 text-[#00BFFF] px-3 py-1 rounded-full uppercase tracking-widest font-bold">{c}</span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
            <span className="inline-block text-[10px] bg-[#FF3B30]/15 text-[#FF3B30] px-3 py-1 rounded-full uppercase tracking-widest font-bold">● Live</span>
            <button data-testid="share-schedule-btn" onClick={() => setShowShare(true)}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-white border border-gray-200 text-gray-700 px-4 py-1.5 rounded-full hover:border-[#00BFFF]/60 transition-colors">
              <Share2 size={14} /> Share schedule
            </button>
            {t.entry_open && (
              <button data-testid="public-enter-btn" onClick={() => { setShowEnter(true); setEntryDone(false); setEntryErr(''); setEntryForm({ team_name: '', manager: '', contact: '', notes: '' }); }}
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#00BFFF] text-black px-4 py-1.5 rounded-full">
                <UserPlus size={14} /> Enter a team{feeLabel(t) ? ` · ${feeLabel(t)}` : ''}
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 px-1 -mx-1">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`pub-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 ${tab === tb.id ? 'bg-[#00BFFF] text-black' : 'bg-[#1C1C1E] text-gray-400 border border-gray-800'}`}>
              {tb.icon} {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'standings' && (
            data.standings.length === 0 ? <Empty msg="No standings yet." /> :
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[1.4rem_1fr_repeat(3,1.6rem)_2rem] gap-1 px-3 py-2.5 text-[10px] text-gray-500 font-bold uppercase border-b border-gray-800">
                <span>#</span><span>Team</span><span className="text-center">P</span><span className="text-center">GD</span><span className="text-center">GF</span><span className="text-center">Pts</span>
              </div>
              {data.standings.map((r, i) => (
                <div key={r.team_id} className="grid grid-cols-[1.4rem_1fr_repeat(3,1.6rem)_2rem] gap-1 px-3 py-3 text-sm border-b border-gray-800/50 last:border-0 items-center">
                  <span className="text-gray-500 text-xs">{i + 1}</span>
                  <span className="font-bold truncate flex items-center gap-2"><Crest name={r.name} logo={r.logo} size={22} />{r.name}</span>
                  <span className="text-center text-gray-400 text-xs">{r.P}</span>
                  <span className="text-center text-gray-400 text-xs">{r.GD}</span>
                  <span className="text-center text-gray-400 text-xs">{r.GF}</span>
                  <span className="text-center font-black text-[#00BFFF]">{r.Pts}</span>
                </div>
              ))}
            </div>
          )}

          {tab === 'fixtures' && (
            data.fixtures.length === 0 ? <Empty msg="No fixtures yet." /> :
            <div className="space-y-2">{data.fixtures.map((f) => <Row key={f.id} f={f} teamName={teamName} teamObj={(tid) => teamMap[tid]} offName={offName} showScore={f.status === 'completed'} />)}</div>
          )}

          {tab === 'overview' && (
            (groupKeys.length === 0 && ko.length === 0) ? <Empty msg="No tournament overview yet. Generate fixtures to see groups, semis and the final." /> :
            <div className="space-y-7">
              {groupKeys.length > 0 && (
                <div>
                  <SectionTitle>Group Stage</SectionTitle>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {groupKeys.map((g) => (
                      <div key={g} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                        <p className="px-3 py-2 text-[11px] font-black uppercase tracking-widest text-[#00BFFF] border-b border-gray-200">Group {g}</p>
                        {data.standings.filter((r) => r.group === g).map((r, i) => (
                          <div key={r.team_id} className="grid grid-cols-[1.2rem_1fr_1.6rem_1.8rem] gap-1 px-3 py-2 text-sm border-b border-gray-100 last:border-0 items-center">
                            <span className="text-gray-400 text-xs">{i + 1}</span>
                            <span className="font-bold truncate flex items-center gap-2"><Crest name={r.name} logo={r.logo} size={20} />{r.name}</span>
                            <span className="text-center text-gray-500 text-xs">{r.P}</span>
                            <span className="text-center font-black text-[#00BFFF]">{r.Pts}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ko.length > 0 && (
                <div>
                  <SectionTitle>Knockouts</SectionTitle>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {koSorted.map(([name, sec]) => (
                      <div key={name} className="min-w-[210px] flex-1 flex flex-col gap-3">
                        <p className="text-[11px] font-black uppercase tracking-widest text-center text-[#00BFFF]">{name}</p>
                        {sec.items.map((f) => <BracketMatch key={f.id} f={f} teamName={teamName} teamObj={(tid) => teamMap[tid]} />)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'safety' && <SafetyPanel src={t} />}

          {tab === 'news' && (
            (data.broadcasts || []).length === 0 ? <Empty msg="No announcements." /> :
            <div className="space-y-2">
              {data.broadcasts.map((b) => (
                <div key={b.id} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Megaphone size={13} className="text-[#00BFFF]" />
                    <span className="text-[9px] uppercase tracking-widest bg-[#00BFFF]/15 text-[#00BFFF] px-2 py-0.5 rounded-full">{b.scope}</span>
                  </div>
                  <p className="text-sm mt-2">{b.message}</p>
                  <p className="text-[10px] text-gray-600 mt-1">{new Date(b.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-gray-600 mt-10 tracking-wider">Powered by Tournevo</p>
      </div>

      {showEnter && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="entry-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-black">Enter a team</h2>
              <button data-testid="entry-close" onClick={() => setShowEnter(false)} className="p-1 text-gray-500"><X size={22} /></button>
            </div>
            {entryDone ? (
              <div className="p-8 text-center" data-testid="entry-success">
                <div className="w-16 h-16 rounded-full bg-[#00D26A]/15 border-2 border-[#00D26A] flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-[#00D26A]" /></div>
                <h3 className="text-xl font-black mb-2">Entry received!</h3>
                <p className="text-sm text-gray-500">Your entry is <b>pending payment</b>. The organiser will confirm it once your entry fee is received, then your team will appear in the tournament.</p>
                <button data-testid="entry-done" onClick={() => setShowEnter(false)} className="mt-6 w-full bg-[#00BFFF] text-black font-bold py-3 rounded-xl">Done</button>
              </div>
            ) : (
              <form onSubmit={submitEntry} className="p-5 space-y-3">
                <div className="bg-[#F4F6F9] border border-gray-200 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Division</p>
                  <p className="text-sm font-bold">{[t.age_group, t.gender === 'girls' ? 'Girls' : 'Mixed', t.standard && `${t.standard} standard`].filter(Boolean).join(' · ') || t.name}</p>
                </div>
                {(t.entry_instructions || feeLabel(t)) && (
                  <div className="bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-xl p-3" data-testid="entry-instructions">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-[#00a6dc] mb-1">How to enter{feeLabel(t) ? ` · ${feeLabel(t)}` : ''}</p>
                    <p className="text-sm whitespace-pre-line text-gray-700">{t.entry_instructions || (Number(t.entry_fee_amount) > 0 ? 'Pay securely by card on the next screen.' : '')}</p>
                  </div>
                )}
                <input data-testid="entry-team-name" required placeholder="Team name" value={entryForm.team_name} onChange={(e) => setEntryForm({ ...entryForm, team_name: e.target.value })} className="w-full bg-[#F4F6F9] border border-gray-200 rounded-xl p-3.5 text-sm" />
                <input data-testid="entry-manager" placeholder="Manager / contact name" value={entryForm.manager} onChange={(e) => setEntryForm({ ...entryForm, manager: e.target.value })} className="w-full bg-[#F4F6F9] border border-gray-200 rounded-xl p-3.5 text-sm" />
                <input data-testid="entry-contact" placeholder="Email or phone" value={entryForm.contact} onChange={(e) => setEntryForm({ ...entryForm, contact: e.target.value })} className="w-full bg-[#F4F6F9] border border-gray-200 rounded-xl p-3.5 text-sm" />
                <textarea data-testid="entry-notes" rows={2} placeholder="Notes (optional)" value={entryForm.notes} onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} className="w-full bg-[#F4F6F9] border border-gray-200 rounded-xl p-3.5 text-sm" />
                {entryErr && <p className="text-[#FF3B30] text-sm" data-testid="entry-error">{entryErr}</p>}
                <button data-testid="entry-submit" disabled={entryBusy} className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
                  {entryBusy && <Loader2 size={16} className="animate-spin" />}{entryBusy ? 'Please wait…' : (Number(t.entry_fee_amount) > 0 ? `Pay ${feeLabel(t)} & enter` : 'Submit entry')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {showChamp && <Celebration champion={champion} onClose={() => { if (champKey) localStorage.setItem(champKey, '1'); setChampDismissed(true); }} />}

      {showShare && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="share-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-black">Share schedule</h2>
              <button data-testid="share-close" onClick={() => setShowShare(false)} className="p-1 text-gray-500"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-500">Send your division's kick-off times &amp; pitches to managers and parents in one tap.</p>
              <select data-testid="share-team-select" value={shareTeam} onChange={(e) => setShareTeam(e.target.value)}
                className="w-full bg-[#F4F6F9] border border-gray-200 rounded-xl p-3 text-sm font-bold">
                <option value="">All teams (full schedule)</option>
                {[...(data.teams || [])].sort((a, b) => a.name.localeCompare(b.name)).map((tm) => (
                  <option key={tm.id} value={tm.id}>{tm.name} only</option>
                ))}
              </select>
              <pre data-testid="share-preview" className="bg-[#F4F6F9] border border-gray-200 rounded-xl p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-52 overflow-y-auto font-sans">{scheduleText}</pre>
              <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(scheduleText)}`} target="_blank" rel="noreferrer"
                className="w-full bg-[#25D366] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2"><Share2 size={16} /> Share on WhatsApp</a>
              {typeof navigator !== 'undefined' && navigator.share && (
                <button data-testid="share-native" onClick={nativeShare} className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl">More share options</button>
              )}
              <button data-testid="share-copy" onClick={copyText} className="w-full bg-[#1C1C1E] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                {shareCopied ? <><Check size={16} className="text-[#00D26A]" /> Copied</> : 'Copy schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {payStatus && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="payment-overlay">
          <div className="bg-white w-full max-w-sm rounded-3xl p-8 text-center">
            {payStatus === 'checking' && (
              <>
                <Loader2 size={40} className="animate-spin text-[#00BFFF] mx-auto mb-4" />
                <h3 className="text-lg font-black mb-1">Confirming payment…</h3>
                <p className="text-sm text-gray-500">Hang tight, this only takes a moment.</p>
              </>
            )}
            {payStatus === 'paid' && (
              <div data-testid="payment-success">
                <div className="w-16 h-16 rounded-full bg-[#00D26A]/15 border-2 border-[#00D26A] flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-[#00D26A]" /></div>
                <h3 className="text-xl font-black mb-2">You're in! 🎉</h3>
                <p className="text-sm text-gray-500 mb-5">Payment received and your team is confirmed in <b>{t.name}</b>. Here's your division link — bookmark it to follow fixtures &amp; standings:</p>
                <div className="bg-[#F4F6F9] border border-gray-200 rounded-xl p-3 text-xs text-gray-700 break-all mb-4">{`${window.location.origin}/live/${id}`}</div>
                <button data-testid="payment-copy" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/live/${id}`)} className="w-full bg-[#00BFFF] text-black font-bold py-3 rounded-xl mb-2">Copy my division link</button>
                <button onClick={() => { setPayStatus(null); window.history.replaceState({}, '', `/live/${id}`); }} className="w-full text-gray-500 font-bold py-2 text-sm">View tournament</button>
              </div>
            )}
            {(payStatus === 'failed' || payStatus === 'timeout') && (
              <>
                <h3 className="text-lg font-black mb-2">Payment not completed</h3>
                <p className="text-sm text-gray-500 mb-5">{payStatus === 'timeout' ? "We couldn't confirm your payment yet. If you were charged, it'll appear shortly." : 'Your payment was cancelled or failed.'}</p>
                <button onClick={() => { setPayStatus(null); window.history.replaceState({}, '', `/live/${id}`); }} className="w-full bg-[#00BFFF] text-black font-bold py-3 rounded-xl">Back to tournament</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Empty = ({ msg }) => <p className="text-center text-gray-600 py-12 text-sm">{msg}</p>;

const SafetyField = ({ label, value, phone }) => (
  <div className="mb-3 last:mb-0">
    <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{label}</p>
    {phone
      ? <a href={`tel:${value}`} className="text-sm font-bold text-gray-900 underline break-words">{value}</a>
      : <p className="text-sm text-gray-700 whitespace-pre-line break-words">{value}</p>}
  </div>
);

export function SafetyPanel({ src }) {
  const s = src || {};
  const hasEmergency = s.nearest_hospital || s.hospital_route || s.first_aider || s.emergency_phone;
  const hasSafeguarding = s.safeguarding_officer || s.safeguarding_phone || s.safeguarding_email || s.safeguarding_notes;
  if (!hasEmergency && !hasSafeguarding) return <Empty msg="No safety information published yet." />;
  return (
    <div className="space-y-4" data-testid="safety-panel">
      {hasEmergency && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="emergency-card">
          <h3 className="text-sm font-black uppercase tracking-widest text-[#FF3B30] flex items-center gap-2 mb-4"><HeartPulse size={16} /> Emergency & medical</h3>
          {s.nearest_hospital && <SafetyField label="Nearest hospital" value={s.nearest_hospital} />}
          {s.hospital_route && <SafetyField label="Route / directions" value={s.hospital_route} />}
          {s.first_aider && <SafetyField label="First aider on site" value={s.first_aider} />}
          {s.emergency_phone && <SafetyField label="Emergency contact" value={s.emergency_phone} phone />}
        </div>
      )}
      {hasSafeguarding && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="safeguarding-card">
          <h3 className="text-sm font-black uppercase tracking-widest text-[#00a6dc] flex items-center gap-2 mb-4"><ShieldAlert size={16} /> Safeguarding</h3>
          {s.safeguarding_officer && <SafetyField label="Safeguarding officer" value={s.safeguarding_officer} />}
          {s.safeguarding_phone && <SafetyField label="Phone" value={s.safeguarding_phone} phone />}
          {s.safeguarding_email && <SafetyField label="Email" value={s.safeguarding_email} />}
          {s.safeguarding_notes && <SafetyField label="How to raise a concern" value={s.safeguarding_notes} />}
        </div>
      )}
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 mb-3">{children}</h3>
);

function BracketMatch({ f, teamName, teamObj }) {
  const oOf = (tid) => teamObj ? teamObj(tid) : null;
  const Side = ({ tid, ph, score }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 truncate text-sm">
        {tid ? <Crest name={teamName(tid)} logo={oOf(tid)?.logo} size={20} /> : <span className="w-5 h-5 rounded-full bg-gray-100 border border-gray-200 shrink-0" />}
        <span className={`truncate ${tid ? 'font-bold' : 'text-gray-400 italic'}`}>{tid ? teamName(tid) : (ph || 'TBD')}</span>
      </span>
      <span className="font-black ml-2 text-sm">{score ?? ''}</span>
    </div>
  );
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-1.5">
      <Side tid={f.home_team_id} ph={f.home_placeholder} score={f.home_score} />
      <div className="h-px bg-gray-100" />
      <Side tid={f.away_team_id} ph={f.away_placeholder} score={f.away_score} />
    </div>
  );
}

function Row({ f, teamName, teamObj, offName, showScore }) {
  const oOf = (tid) => teamObj ? teamObj(tid) : null;
  const homeLabel = f.home_team_id ? teamName(f.home_team_id) : (f.home_placeholder || 'TBD');
  const awayLabel = f.away_team_id ? teamName(f.away_team_id) : (f.away_placeholder || 'TBD');
  const ref = f.assignments?.referee ? (offName ? offName(f.assignments.referee) : '') : '';
  return (
    <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3">
      {(f.kickoff || f.pitch || ref) && (
        <div className="flex items-center gap-3 mb-2 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
          {f.kickoff && <span className="flex items-center gap-1"><Clock size={11} />{f.kickoff}</span>}
          {f.pitch && <span className="flex items-center gap-1"><MapPin size={11} />{f.pitch}</span>}
          {ref && <span className="flex items-center gap-1"><UserCog size={11} />{ref}</span>}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className={`text-sm flex-1 truncate flex items-center gap-2 ${f.home_team_id ? 'font-bold' : 'text-gray-500 italic'}`}><Crest name={homeLabel} logo={oOf(f.home_team_id)?.logo} size={24} />{homeLabel}</span>
        {showScore ? <span className="flex items-center gap-1.5"><span className="text-[8px] font-black text-[#00D26A] border border-[#00D26A]/50 rounded px-1 py-0.5 uppercase tracking-wider">FT</span><span className="font-black bg-black px-3 py-1 rounded-lg text-sm">{f.home_score} - {f.away_score}</span></span> : <span className="text-gray-600 text-xs px-3">vs</span>}
        <span className={`text-sm flex-1 truncate text-right flex items-center justify-end gap-2 ${f.away_team_id ? 'font-bold' : 'text-gray-500 italic'}`}>{awayLabel}<Crest name={awayLabel} logo={oOf(f.away_team_id)?.logo} size={24} /></span>
      </div>
      {f.summary && showScore && <p className="text-[11px] text-gray-500 italic mt-2">{f.summary}</p>}
    </div>
  );
}
